class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
  }

  connectedCallback() {
    this.initDeliveryEstimate();

    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // Save opener only when opening
        if (!this.isOpen) this.rememberOpener(e);

        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      const deliveryTrigger = e.target.closest(".cart-delivery-estimate__trigger");
      const deliveryTooltip = this.querySelector(".cart-delivery-estimate__tooltip");

      if (deliveryTrigger && deliveryTooltip) {
        e.preventDefault();
        const willOpen = deliveryTrigger.getAttribute("aria-expanded") !== "true";
        deliveryTrigger.setAttribute("aria-expanded", String(willOpen));
        deliveryTooltip.hidden = !willOpen;
        return;
      }

      if (deliveryTooltip) {
        deliveryTooltip.hidden = true;
        this.querySelector(".cart-delivery-estimate__trigger")?.setAttribute("aria-expanded", "false");
      }

      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    this.initDeliveryEstimate();

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  initDeliveryEstimate() {
    const estimate = this.querySelector("[data-cart-delivery-estimate]");
    if (!estimate) return;

    const text = estimate.querySelector("[data-cart-delivery-text]");
    const tooltip = estimate.querySelector(".cart-delivery-estimate__tooltip");
    const preorderVariantIds = estimate.dataset.preorderVariantIds
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    const cartItemCount = Number(estimate.dataset.cartItemCount);
    const preorderItemCount = Number(estimate.dataset.preorderItemCount);
    const stockItemCount = cartItemCount - preorderItemCount;
    const trigger = estimate.querySelector(".cart-delivery-estimate__trigger");

    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}/${date.getFullYear()}`;
    };

    const getNextDeliveryDate = () => {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      date.setDate(date.getDate() + 2);
      if (date.getDay() === 0) date.setDate(date.getDate() + 1);
      return date;
    };

    const parsePreorderDate = (message) => {
      const normalizedMessage = message
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();
      const match = normalizedMessage.match(
        /(\d{1,2})\s+(JANVIER|FEVRIER|MARS|AVRIL|MAI|JUIN|JUILLET|AOUT|SEPTEMBRE|OCTOBRE|NOVEMBRE|DECEMBRE)\s+(\d{4})/,
      );
      if (!match) return null;

      const months = [
        "JANVIER",
        "FEVRIER",
        "MARS",
        "AVRIL",
        "MAI",
        "JUIN",
        "JUILLET",
        "AOUT",
        "SEPTEMBRE",
        "OCTOBRE",
        "NOVEMBRE",
        "DECEMBRE",
      ];
      return new Date(Number(match[3]), months.indexOf(match[2]), Number(match[1]), 12);
    };

    const renderEstimate = (attempt = 0) => {
      if (!estimate.isConnected || this.querySelector("[data-cart-delivery-estimate]") !== estimate) return;

      if (!preorderVariantIds.length) {
        text.textContent = `Livraison estimée le ${formatDate(getNextDeliveryDate())}`;
        trigger.hidden = true;
        trigger.setAttribute("aria-expanded", "false");
        tooltip.hidden = true;
        return;
      }

      trigger.hidden = false;

      const preorderSettings =
        window.Globo?.Preorder?.settings?.preorderSettings || window.GloboPreorderParams?.preorderSettings;

      if ((!preorderSettings?.products || !preorderSettings?.profiles) && attempt < 40) {
        setTimeout(() => renderEstimate(attempt + 1), 100);
        return;
      }

      const dates = preorderVariantIds
        .map((variantId) => {
          let profileId;
          Object.values(preorderSettings?.products || {}).some((variants) => {
            profileId = variants?.[variantId];
            return Boolean(profileId);
          });

          const profile = preorderSettings?.profiles?.[profileId];
          if (!profile?.message) return null;

          try {
            const messages = JSON.parse(profile.message);
            const localizedMessage = messages.fr || messages.default || Object.values(messages)[0];
            return parsePreorderDate(localizedMessage?.aboveMessage || "");
          } catch (error) {
            return null;
          }
        })
        .filter(Boolean)
        .sort((firstDate, secondDate) => secondDate - firstDate);

      const preorderDate = dates.length ? formatDate(dates[0]) : null;
      text.textContent = preorderDate
        ? `L'expédition de votre commande est prévue à partir du ${preorderDate}`
        : "L'expédition de votre commande est prévue à partir de la date indiquée sur la fiche produit";
      tooltip.textContent = stockItemCount > 0
        ? "Votre commande contient un article en précommande et un article en stock. Elle sera expédiée lorsque tous les articles seront disponibles."
        : preorderDate
          ? `Votre commande contient un article en précommande. Elle sera expédiée à partir du ${preorderDate}.`
          : "Votre commande contient un article en précommande. Elle sera expédiée à partir de la date indiquée sur la fiche produit.";
    };

    renderEstimate();
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}






customElements.define("cart-drawer-items", CartDrawerItems);

